import { MarkerClusterer, GridAlgorithm } from "@googlemaps/markerclusterer"; // Make sure you installed it
import { Location } from "../types/mapData";

// --- Marker Utilities ---

/**
 * Clears all markers from the map and resets the marker state.
 *
 * @param markers - Existing markers
 * @param setMarkers - Function to update marker state
 */
export function clearMarkers(
  markers: google.maps.Marker[],
  setMarkers: (markers: google.maps.Marker[]) => void,
) {
  if (!markers.length) return;

  markers.forEach((marker) => marker.setMap(null));
  setMarkers([]);
}

/**
 * Builds a single Google Maps marker from a location object.
 *
 * @param loc - Location data
 * @param map - Map instance
 * @param ovalUrl - Marker icon URL
 * @param attachToMap - Whether to immediately attach to map
 * @returns Google Maps Marker instance
 */
export function buildSingleMarker(
  loc: any,
  map: google.maps.Map,
  ovalUrl: string,
  attachToMap: boolean = true,
): google.maps.Marker {
  const marker = new google.maps.Marker({
    position: {
      lat: loc.location.latitude,
      lng: loc.location.longitude,
    },
    map: attachToMap ? map : undefined,
    icon: {
      url: ovalUrl,
      labelOrigin: new google.maps.Point(15.5, 14),
    },
    optimized: false,
  });

  // Attach extra metadata
  (marker as any).location = loc;
  (marker as any).locName = loc.name;
  (marker as any).brand_id = loc.brand_id;
  const { address, city, state, zip_code } = loc.location || {};
  (marker as any).address = [
    address,
    city,
    [state, zip_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return marker;
}

/**
 * Builds an array of Google Maps Markers from location data.
 *
 * Responsibilities:
 *  - Loops over provided locations
 *  - For each valid location:
 *    - Creates a new marker (optionally detached from map)
 *    - Attaches the created marker back onto the location object
 *  - Returns the full list of markers
 *
 * @param locations - Array of location objects with latitude and longitude fields
 * @param map - Google Maps instance where markers will (optionally) be attached
 * @param ovalUrl - Marker icon URL (example: oval.svg)
 * @param skipMapAssign - If true, markers will not be assigned to the map immediately (for clustering)
 * @returns Array of created Google Maps Markers
 */
export function buildMarkersFromLocations(
  locations: Location[],
  map: google.maps.Map,
  ovalUrl: string,
  skipMapAssign: boolean = false,
): google.maps.Marker[] {
  const markers: google.maps.Marker[] = [];

  locations.forEach((loc) => {
    // Guard against missing or invalid coordinates
    if (!loc?.location?.latitude || !loc?.location?.longitude) {
      console.warn("[Markers] Skipping invalid location:", loc);
      return;
    }

    // Build a single marker (optionally not attached to map immediately)
    const marker = buildSingleMarker(loc, map, ovalUrl, !skipMapAssign);

    // Attach marker reference back onto the location object
    loc.marker = marker;

    // Add marker to collection
    markers.push(marker);
  });

  groupOverlappingMarkers(markers);

  return markers;
}

// --- Marker Clustering Utility ---

/**
 * Creates or updates a MarkerClusterer instance.
 *
 * @param options - Map, markers, and cluster styling info
 * @returns MarkerClusterer instance
 */
export function createOrUpdateMarkerCluster({
  map,
  markers,
  ovalUrl,
}: {
  map: google.maps.Map;
  markers: google.maps.Marker[];
  ovalUrl: string;
}): MarkerClusterer {
  if ((window as any).currentMarkerCluster) {
    (window as any).currentMarkerCluster.clearMarkers();
  }

  const clusterer = new MarkerClusterer({
    map,
    markers,
    algorithmOptions: {
      maxZoom: 15,
    },
    renderer: {
      render: ({ count, position, markers }) => {
        const marker = new google.maps.Marker({
          position,
          icon: {
            url: ovalUrl,
            labelOrigin: new google.maps.Point(15.5, 14),
          },
          label: {
            text: String(markers?.length),
            className: "map-v2-label",
          },
          zIndex: 1000 + count,
        });
        (marker as any).setOpacity(0); // Start invisible

        // Animate the marker in after a short delay
        setTimeout(() => {
          (marker as any).setOpacity(1);
        }, 100); // 100ms fade in — tweak if you want

        return marker;
      },
    },
    onClusterClick: (_, cluster) => {
      const markersInCluster = cluster?.markers;
      if (!markersInCluster || markersInCluster.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      markersInCluster.forEach((marker) => bounds.extend(marker.getPosition()));

      map.panTo(bounds.getCenter());
      map.setOptions({ maxZoom: 16 });
      map.fitBounds(bounds);
      google.maps.event.addListenerOnce(map, "idle", () => {
        map.setOptions({ maxZoom: 20 });
      });
    },
  });

  (window as any).currentMarkerCluster = clusterer;
  return clusterer;
}

export function groupOverlappingMarkers(markers: google.maps.Marker[]): void {
  const latLngs: Record<string, number[]> = {}; // ~111m tolerance

  markers.forEach((marker, index) => {
    const pos = marker.getPosition();
    if (!pos) return;

    // Rounded key for grouping
    const key = `${pos.lat()}${pos.lng()}`;
    if (latLngs[key]) {
      latLngs[key].push(index);
    } else {
      latLngs[key] = [index];
    }

    // Save rounded key for later reference
    (marker as any).latlngGroupKey = key;
  });

  for (const coord in latLngs) {
    const indices = latLngs[coord];
    if (indices.length > 1) {
      for (let i = 0; i < indices.length; i++) {
        const m = markers[indices[i]];
        (m as any).isMulti = true;
        (m as any).multiLoc = coord;
        (m as any).multiLocations = indices.length;
        m.setLabel({
          text: String(indices.length),
          className: "map-v2-label",
        });
        if (i !== 0) {
          m.setVisible(false);
          (m as any).location.marker = markers[indices[0]];
        }
      }
    }
  }
}
